import { AuthError } from '@azure/msal-browser'
import { z } from 'zod'

import { ApiError } from '@/shared/api/api-error'
import { forgetSignInFailure, keepSignInFailure, readKeptSignInFailure } from '@/shared/auth/kept-sign-in-failure'
import { isRoutePath } from '@/shared/auth/sign-in-hop'

/**
 * Why a sign-in B2C sent back failed, as far as the person can act on it: they chose "Forgot your
 * password?", which TMS's own page handles; it was canceled or expired, so starting again works;
 * the site's sign-in setup is wrong, so only a fix helps; or something else.
 */
export type SignInFailureKind = 'forgotPassword' | 'interrupted' | 'setup' | 'other'

// B2C's answer when the person chose "Forgot your password?" on its sign-in page.
const forgotPasswordB2CCode = 'AADB2C90118'

export interface SignInFailure {
  readonly kind: SignInFailureKind
  /** What MSAL or B2C said: the error code and description, with B2C's correlation id and timestamp. */
  readonly details: string
}

// B2C's own codes, which name the cause more exactly than the OAuth error they come with.
const interruptedB2CCodes = new Set([
  'AADB2C90091', // the person canceled
  'AADB2C90080', // the grant expired
])

const setupB2CCodes = new Set([
  'AADB2C90006', // redirect URI not registered for the application
  'AADB2C90007', // the application has no redirect URI registered
  'AADB2C90011', // client id does not match
  'AADB2C90012', // scope not valid
  'AADB2C90055', // scope must include openid
  'AADB2C90057', // the application is not configured for this flow
  'AADB2C90058', // the application is not configured for public clients
  'AADB2C90068', // the application is not valid against this service
  'AADB2C90079', // the client must send a secret
  'AADB2C90117', // scope not supported
])

// OAuth errors from B2C and MSAL's own codes.
const interruptedCodes = new Set([
  'access_denied',
  'user_cancelled',
  'user_canceled',
  'invalid_grant',
  'login_required',
  'interaction_required',
  'state_mismatch',
  'state_not_found',
  'invalid_state',
  'nonce_mismatch',
  'no_state_in_hash',
  'unable_to_parse_state',
  'no_token_request_cache_error',
  'unable_to_parse_token_request_cache_error',
  'timed_out',
])

const setupCodes = new Set([
  'redirect_uri_mismatch',
  'unauthorized_client',
  'invalid_client',
  'invalid_request',
  'invalid_scope',
  'unsupported_response_type',
  'redirect_uri_empty',
  'untrusted_authority',
  'authority_mismatch',
  'issuer_validation_failed',
  'invalid_authority_metadata',
])

function kindOf(errorCode: string, description: string): SignInFailureKind {
  const b2cCode = /\bAADB2C\d+\b/.exec(description)?.[0]
  if (b2cCode === forgotPasswordB2CCode) {
    return 'forgotPassword'
  }
  if (b2cCode !== undefined && interruptedB2CCodes.has(b2cCode)) {
    return 'interrupted'
  }
  if (b2cCode !== undefined && setupB2CCodes.has(b2cCode)) {
    return 'setup'
  }
  if (interruptedCodes.has(errorCode)) {
    return 'interrupted'
  }
  return setupCodes.has(errorCode) ? 'setup' : 'other'
}

// Far more than B2C's code, description, correlation id and timestamp take.
const detailsLimit = 2_000

function capDetails(details: string): string {
  return details.length > detailsLimit ? `${details.slice(0, detailsLimit)}…` : details
}

const failureSchema = z.object({
  kind: z.enum(['forgotPassword', 'interrupted', 'setup', 'other']),
  details: z.string().transform(capDetails),
})

/**
 * Before first render: the failure the callback shows. A new one is kept for this tab, and the
 * address moves to the callback, the only page that shows it. A reload of the callback with no new
 * answer shows the kept one again; any other start forgets it. `isAnswered`: a sign-in answer, or a
 * hop's, was in the address.
 */
export function settleSignInFailure(
  failure: SignInFailure | undefined,
  redirectPath: string,
  isAnswered: boolean,
): SignInFailure | undefined {
  const isCallback = isRoutePath(redirectPath, window.location.pathname)
  if (failure !== undefined) {
    const shown = { kind: failure.kind, details: capDetails(failure.details) }
    keepSignInFailure(shown)
    if (!isCallback) {
      window.history.replaceState(window.history.state, '', redirectPath)
    }
    return shown
  }
  if (isCallback && !isAnswered) {
    const kept = failureSchema.safeParse(readKeptSignInFailure())
    if (kept.success) {
      return kept.data
    }
  }
  forgetSignInFailure()
  return undefined
}

/** Sorts what consuming the sign-in response threw, keeping its text for support. */
export function describeSignInFailure(error: unknown): SignInFailure {
  if (error instanceof AuthError) {
    return { kind: kindOf(error.errorCode, error.errorMessage), details: error.message }
  }
  const details = describeOtherError(error)
  return { kind: kindOf('', details), details }
}

const unreadableAnswer = 'The sign-in answer could not be read.'

/**
 * The text support needs from a failure that is not B2C's own, such as a hop's or a sign-in link's,
 * with nothing an answer carried: an API refusal gives only its status, code and correlation id; an
 * API out of reach, or an answer that could not be read, only says so.
 */
export function describeOtherError(error: unknown): string {
  if (error instanceof ApiError && error.status === 0) {
    return 'The API could not be reached.'
  }
  // A successful status whose body could not be read.
  if (error instanceof z.ZodError || (error instanceof ApiError && error.status < 300)) {
    return unreadableAnswer
  }
  if (error instanceof ApiError) {
    const refusal = `The API refused the sign-in (HTTP ${String(error.status)}${error.code === null ? '' : `, ${error.code}`}).`
    return error.correlationId === null ? refusal : `${refusal}\r\nCorrelation ID: ${error.correlationId}`
  }
  return error instanceof Error ? error.message : String(error)
}
